import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Users,
  Building2,
  MessageCircle
} from 'lucide-react';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [parentForm, setParentForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  
  const [partnerForm, setPartnerForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  
  const [isSubmittingParent, setIsSubmittingParent] = useState(false);
  const [isSubmittingPartner, setIsSubmittingPartner] = useState(false);

  const handleParentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingParent(true);
    
    try {
      const response = await axios.post(`${API}/contact/parent`, parentForm);
      toast.success(response.data.message);
      setParentForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting parent enquiry:', error);
      toast.error('Failed to send message. Please try again or email us directly at hello@jollytotsrides.co.za');
    } finally {
      setIsSubmittingParent(false);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingPartner(true);
    
    try {
      const response = await axios.post(`${API}/contact/partner`, partnerForm);
      toast.success(response.data.message);
      setPartnerForm({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting partner enquiry:', error);
      toast.error('Failed to send enquiry. Please try again or email us directly at hello@jollytotsrides.co.za');
    } finally {
      setIsSubmittingPartner(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient py-16 md:py-24">
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-[hsl(45,98%,80%)] opacity-50 sun-pulse" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold jolly-text-teal mb-6">
              Get in Touch
            </h1>
            <p className="text-gray-600 text-lg md:text-xl">
              We'd love to hear from you! Whether you're a parent with questions or a mall interested in partnering.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Strip */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 text-gray-600 hover:jolly-text-orange transition-colors">
              <Mail className="w-5 h-5 jolly-text-orange" />
              <span className="font-medium">{contactInfo.email}</span>
            </a>
            <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 text-gray-600 hover:jolly-text-orange transition-colors">
              <Phone className="w-5 h-5 jolly-text-orange" />
              <span className="font-medium">{contactInfo.phone}</span>
            </a>
            <div className="flex items-center gap-3 text-gray-600">
              <MapPin className="w-5 h-5 jolly-text-orange" />
              <span className="font-medium">{contactInfo.address}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Forms Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Parents Form */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[hsl(33,100%,90%)] flex items-center justify-center">
                  <Users className="w-6 h-6 jolly-text-orange" />
                </div>
                <h2 className="text-2xl font-bold jolly-text-teal">For Parents</h2>
              </div>
              
              <Card className="shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <p className="text-gray-600 mb-6">
                    Have a question about our service? Want to know where to find us? We're here to help!
                  </p>
                  <form onSubmit={handleParentSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="parent-name">Name</Label>
                      <Input
                        id="parent-name"
                        value={parentForm.name}
                        onChange={(e) => setParentForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your name"
                        required
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="parent-email">Email</Label>
                      <Input
                        id="parent-email"
                        type="email"
                        value={parentForm.email}
                        onChange={(e) => setParentForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="your@email.com"
                        required
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="parent-message">Message</Label>
                      <Textarea
                        id="parent-message"
                        value={parentForm.message}
                        onChange={(e) => setParentForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="How can we help you?"
                        rows={4}
                        required
                        className="rounded-xl"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="jolly-btn-orange w-full"
                      disabled={isSubmittingParent}
                    >
                      {isSubmittingParent ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Partners Form */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[hsl(210,90%,90%)] flex items-center justify-center">
                  <Building2 className="w-6 h-6 jolly-text-blue" />
                </div>
                <h2 className="text-2xl font-bold jolly-text-teal">For Malls & Partners</h2>
              </div>
              
              <Card className="shadow-lg">
                <CardContent className="p-6 md:p-8">
                  <p className="text-gray-600 mb-6">
                    Interested in bringing Jolly Tots Rides to your shopping centre? Let's discuss a partnership.
                  </p>
                  <form onSubmit={handlePartnerSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="partner-name">Name</Label>
                        <Input
                          id="partner-name"
                          value={partnerForm.name}
                          onChange={(e) => setPartnerForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Your name"
                          required
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="partner-email">Email</Label>
                        <Input
                          id="partner-email"
                          type="email"
                          value={partnerForm.email}
                          onChange={(e) => setPartnerForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="your@email.com"
                          required
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="partner-company">Mall or Company</Label>
                      <Input
                        id="partner-company"
                        value={partnerForm.company}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, company: e.target.value }))}
                        placeholder="Company name"
                        required
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="partner-message">Message</Label>
                      <Textarea
                        id="partner-message"
                        value={partnerForm.message}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Tell us about your interest..."
                        rows={4}
                        className="rounded-xl"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="jolly-btn-blue w-full"
                      disabled={isSubmittingPartner}
                    >
                      {isSubmittingPartner ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Partnership Enquiry
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Friendly Message Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block p-4 bg-white rounded-full shadow-lg mb-6">
            <MessageCircle className="w-10 h-10 jolly-text-orange" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold jolly-text-teal mb-4">
            We're Here to Help
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            At Jolly Tots Rides, we're passionate about making family shopping easier and more enjoyable. Whether you have a quick question or want to discuss a partnership opportunity, our friendly team is always happy to chat.
          </p>
          <div className="mt-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_f4ef9e04-9139-4323-b854-e5d21a16af08/artifacts/l5hnk185_Jolly%20Bear%201.png"
              alt="Jolly Bear Mascot"
              className="w-28 h-auto mx-auto"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
